import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import './static/css/App.css';
import Nav from './components/Nav';
import ParticleBackground from './components/ParticleBackground';
import Loading from './components/Loading';
import useGTM from './hooks/useGTM';

const About = lazy(() => import('./pages/About'));
const Project = lazy(() => import('./pages/Project'));
const Knowledge = lazy(() => import('./pages/Knowledge'));
const Diploma = lazy(() => import('./pages/Diploma'));
const Experience = lazy(() => import('./pages/Experience'));

let hasPlayedLoading = false;

const EASE = [0.25, 0.1, 0.25, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.45, ease: EASE, when: 'beforeChildren', staggerChildren: 0.12 },
  },
};
const contentVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE },
  },
};
const pageVariants = {
  initial: { opacity: 0, y: 18 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE },
  },
  exit: { opacity: 0, y: -14, transition: { duration: 0.3, ease: EASE } },
};

function PageTracker() {
  const location = useLocation();
  const { trackPageView } = useGTM();
  const { t } = useTranslation();
  useEffect(() => {
    const pathToTitle = {
      '/': t('sobreT'),
      '/tecnologias': t('tecnologiasT'),
      '/projetos': t('projetosT'),
      '/certificado': t('certificadoT'),
      '/trabalho': t('experienciaT'),
    };
    trackPageView(pathToTitle[location.pathname] || 'Página não encontrada', location.pathname);
  }, [location, t, trackPageView]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const { t } = useTranslation();
  const [showLoading, setShowLoading] = useState(() => !hasPlayedLoading);

  const handleLoadingDone = useCallback(() => {
    hasPlayedLoading = true;
    setShowLoading(false);
  }, []);

  if (showLoading) {
    return <Loading onDone={handleLoadingDone} />;
  }

  return (
    <>
      <ParticleBackground />
      <motion.div
        className="appContainer"
        variants={containerVariants}
        initial="hidden"
        animate={'visible'}
      >
        <PageTracker />
        <Nav />
        <Suspense fallback={<div style={{ flex: 1 }} />}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              className="App"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <Routes>
                <Route
                  path="/"
                  element={
                    <motion.div variants={contentVariants}>
                      <About />
                    </motion.div>
                  }
                />
                <Route
                  path="/tecnologias"
                  element={
                    <motion.div variants={contentVariants}>
                      <Knowledge />
                    </motion.div>
                  }
                />
                <Route
                  path="/projetos"
                  element={
                    <motion.div variants={contentVariants}>
                      <h1>{t('projetosT')}</h1>
                      <Project />
                    </motion.div>
                  }
                />
                <Route
                  path="/certificado"
                  element={
                    <motion.div variants={contentVariants}>
                      <h1>{t('certificadoT')}</h1>
                      <Diploma />
                    </motion.div>
                  }
                />
                <Route
                  path="/trabalho"
                  element={
                    <motion.div variants={contentVariants}>
                      <Experience />
                    </motion.div>
                  }
                />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </motion.div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
